import { API_URL } from './api';
import { authApi } from './auth';

const getAuthHeaders = () => {
  const token = authApi.getToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export const adminApi = {
  async fetchEntries(endpoint, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_URL}${endpoint}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  },

  async createEntry(endpoint, data) {
    try {
      console.log('Creating entry:', { endpoint, data }); // Debug log
      
      // Clean data and convert media objects to just their IDs
      const cleanData = { ...data };
      
      // Remove system fields
      const systemFields = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt', 'locale', 'localizations', 'createdBy', 'updatedBy'];
      systemFields.forEach(field => {
        delete cleanData[field];
      });
      
      // Convert media fields and relations to just their IDs
      Object.keys(cleanData).forEach(key => {
        const value = cleanData[key];
        
        // Single media field (like mainImage, document, etc.)
        if (value && typeof value === 'object' && value.id && !Array.isArray(value)) {
          cleanData[key] = value.id;
        }
        
        // Multiple media fields (like images, videos, etc.)
        if (Array.isArray(value) && value.length > 0 && value[0] && typeof value[0] === 'object' && value[0].id) {
          cleanData[key] = value.map(item => item.id);
        }
        
        // Relations (like linkedEvents) - convert to documentIds
        if (key === 'linkedEvents' && Array.isArray(value) && value.length > 0) {
          cleanData[key] = value.map(item => item.documentId || item.id);
        }
      });
      
      console.log('Final create data being sent to API:', cleanData);
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ data: cleanData }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Create error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Create error:', error);
      throw error;
    }
  },

  async updateEntry(endpoint, id, data) {
    try {
      console.log('Updating entry:', { endpoint, id, data }); // Debug log
      
      // Clean data and convert media objects to just their IDs
      const cleanData = { ...data };
      
      // Remove system fields
      const systemFields = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt', 'locale', 'localizations', 'createdBy', 'updatedBy'];
      systemFields.forEach(field => {
        delete cleanData[field];
      });
      
      // Convert media fields and relations to just their IDs
      Object.keys(cleanData).forEach(key => {
        const value = cleanData[key];
        
        // Single media field (like mainImage, document, etc.)
        if (value && typeof value === 'object' && value.id && !Array.isArray(value)) {
          cleanData[key] = value.id;
        }
        
        // Multiple media fields (like images, videos, etc.)
        if (Array.isArray(value) && value.length > 0 && value[0] && typeof value[0] === 'object' && value[0].id) {
          cleanData[key] = value.map(item => item.id);
        }
        
        // Relations (like linkedEvents) - convert to documentIds
        if (key === 'linkedEvents' && Array.isArray(value) && value.length > 0) {
          cleanData[key] = value.map(item => item.documentId || item.id);
        }
      });
      
      console.log('Final data being sent to API:', cleanData);
      
      const response = await fetch(`${API_URL}${endpoint}/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ data: cleanData }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Update error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Update error:', error);
      throw error;
    }
  },

  async updateSingleType(endpoint, data) {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ data }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Update single type error:', error);
      throw error;
    }
  },

  async deleteEntry(endpoint, id) {
    try {
      const response = await fetch(`${API_URL}${endpoint}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Delete error:', error);
      throw error;
    }
  },

  async uploadFile(file) {
    try {
      const formData = new FormData();
      formData.append('files', file);

      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authApi.getToken()}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },

  // User management methods
  async fetchUsers() {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch users error:', error);
      throw error;
    }
  },

  async createUser(userData) {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  },

  async updateUser(userId, userData) {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  },

  async deleteUser(userId) {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  },

  async getCurrentUser() {
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }
};